/**
 * 新建空白文档（DESIGN.md §2.6 补充：「文件 → 新建」对话框）。
 *
 * 跟真实 Photoshop 的「新建文档」一致：默认背景是纯白不透明图层，不是
 * 空文档/透明图层——用户十有八九是要在白纸上画画，不是要一个什么都
 * 没有的容器；背景色也支持对话框里改（默认白）。像素写入方式与
 * image-import.ts 的 documentFromImage 同一套按 tile 分块逐块 fill 的
 * 路数（CLAUDE.md 规则 6：像素只归 TileStore）。
 */
import type { Document, RasterLayer } from '../../document/types';
import { createDocument, layerDefaults } from '../../document/draft';
import { TILE, layerId, tileKey } from '../../engine/tile/key';
import type { TileStore } from '../../engine/tile/store';

export interface NewDocumentOptions {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly title: string;
  readonly createdAt: string;
  /** sRGB 0–255，`transparent` 为 true 时忽略。省略 = 白。 */
  readonly background?: readonly [number, number, number] | undefined;
  /** 背景图层整片 alpha=0（跟 Photoshop「新建」的"透明"背景内容一致）。 */
  readonly transparent?: boolean | undefined;
}

/** 新建一个带纯色（或透明）背景图层的文档，像素写入 tiles。 */
export function createBlankDocument(tiles: TileStore, options: NewDocumentOptions): Document {
  const { width, height } = options;
  const [r, g, b] = options.background ?? [255, 255, 255];
  const a = options.transparent === true ? 0 : 255;
  const id = layerId(`${options.id}:bg`);
  const tilesX = Math.ceil(width / TILE);
  const tilesY = Math.ceil(height / TILE);

  const buffer = new Uint8Array(TILE * TILE * 4);
  for (let i = 0; i < buffer.length; i += 4) {
    buffer[i] = r;
    buffer[i + 1] = g;
    buffer[i + 2] = b;
    buffer[i + 3] = a;
  }
  for (let ty = 0; ty < tilesY; ty++) {
    for (let tx = 0; tx < tilesX; tx++) {
      // set() 内部会复制，同一块白色缓冲可以整块直接复用——不用像
      // documentFromImage 那样每块重新 fill，反正每块内容完全一样
      tiles.set(tileKey(id, tx, ty), buffer);
    }
  }

  const layer: RasterLayer = {
    ...layerDefaults(id, '背景'),
    kind: 'raster',
    tiles: tiles.handle(id),
    bounds: { x: 0, y: 0, width, height },
  };

  const base = createDocument({
    id: options.id,
    width,
    height,
    title: options.title,
    createdAt: options.createdAt,
  });
  return { ...base, root: { ...base.root, children: [layer] } };
}
